<script setup lang="ts">
  import { ref, onMounted, onUnmounted, computed } from 'vue'

  const props = defineProps({
    multiple: {
      type: Boolean,
      default: false,
    },
    maxFiles: {
      type: Number,
      default: 20,
    },
    selectedIndex: {
      type: Number,
      default: null,
    },
    itemStatuses: {
      type: Array as () => Array<'pending' | 'uploading' | 'done' | 'error'>,
      default: () => [],
    },
    primaryIndex: {
      type: Number,
      default: null,
    },
  })

  const emit = defineEmits(['select', 'set-primary'])

  // Single-file model (backward-compatible)
  const file = defineModel('file', {
    type: File,
    default: null,
  })

  // Multi-file model
  const files = defineModel('files', {
    type: Array,
    default: () => [],
  })

  const active = ref(false)
  let inActiveTimeout: ReturnType<typeof setTimeout>|undefined = undefined
  const events = ['dragenter', 'dragover', 'dragleave', 'drop']

  function onDrop(e: DragEvent) {
    setInactive()

    if (props.multiple) {
      const dropped = Array.from(e.dataTransfer!.files) as File[]
      addFiles(dropped)
    } else {
      if (e.dataTransfer!.files.length > 1) {
        alert('You can only upload one file at a time')
        return
      }
      file.value = e.dataTransfer!.files[0]
    }
  }

  function onInputChange(e: Event) {
    const target = e.target as HTMLInputElement
    if (props.multiple) {
      const selected = Array.from(target.files!) as File[]
      addFiles(selected)
      target.value = ''
    } else {
      if (target.files!.length > 1) {
        alert('You can only upload one file at a time')
        return
      }
      file.value = target.files![0]
    }
  }

  function addFiles(newFiles: File[]) {
    const currentCount = (files.value as File[]).length
    const remaining = props.maxFiles - currentCount

    if (remaining <= 0) {
      alert(`Maximal ${props.maxFiles} Dateien erlaubt.`)
      return
    }

    if (newFiles.length > remaining) {
      alert(`Es können nur noch ${remaining} Dateien hinzugefügt werden (max. ${props.maxFiles}).`)
      newFiles = newFiles.slice(0, remaining)
    }

    files.value = [...(files.value as File[]), ...newFiles]
  }

  function removeFile(index: number) {
    const arr = [...(files.value as File[])]
    arr.splice(index, 1)
    files.value = arr
  }

  function preventDefaults(e: Event) {
    e.preventDefault()
  }

  function setActive() {
    active.value = true
    clearTimeout(inActiveTimeout)
  }

  function setInactive() {
    inActiveTimeout = setTimeout(() => {
      active.value = false
    }, 100)
  }

  const imgSrc = computed(() => {
    return file.value ? URL.createObjectURL(file.value) : ''
  })

  function fileSrc(f: File) {
    return URL.createObjectURL(f)
  }

  function formatSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  onMounted(() => {
    events.forEach((eventName) => {
      document.body.addEventListener(eventName, preventDefaults)
    })
  })

  onUnmounted(() => {
    events.forEach((eventName) => {
      document.body.removeEventListener(eventName, preventDefaults)
    })
  })
</script>

<template>
  <!-- SINGLE FILE MODE (backward-compatible) -->
  <div v-if="!multiple" class="relative" :data-active="active" @dragenter.prevent="setActive" @dragover.prevent="setActive" @dragleave.prevent="setInactive" @drop.prevent="onDrop">
    <div class="card w-full bg-base-200">
      <div class="card-body relative">
        <div class="flex flex-col justify-center items-center w-full min-h-[550px]">
          <div class="flex flex-col justify-center items-center" v-if="file === null">
            <svg class="h-10 w-10" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><title>upload</title><path d="M9,16V10H5L12,3L19,10H15V16H9M5,20V18H19V20H5Z" /></svg>
            <div class="my-4">Bild hier reinziehen oder Datei auswählen</div>
            <input type="file" class="file-input file-input-bordered" @change.prevent="onInputChange"/>
          </div>
          <div v-else>
            <img :src="imgSrc" class="h-full w-auto" />
            <button class="btn btn-square btn-error absolute top-0 right-0 mt-3 mr-3" @click.prevent="file = null">
              <svg class="w-8 h-8 fill-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><title>Bild entfernen</title><path d="M13 19C13 19.7 13.13 20.37 13.35 21H5C3.9 21 3 20.11 3 19V5C3 3.9 3.9 3 5 3H19C20.11 3 21 3.9 21 5V13.35C20.37 13.13 19.7 13 19 13V5H5V19H13M11.21 15.83L9.25 13.47L6.5 17H13.35C13.75 15.88 14.47 14.91 15.4 14.21L13.96 12.29L11.21 15.83M22.54 16.88L21.12 15.47L19 17.59L16.88 15.47L15.47 16.88L17.59 19L15.47 21.12L16.88 22.54L19 20.41L21.12 22.54L22.54 21.12L20.41 19L22.54 16.88Z" /></svg>
            </button>
          </div>
          <slot :dropZoneActive="active"></slot>
        </div>
      </div>
    </div>
    <div class="absolute top-0 bottom-0 left-0 right-0" v-if="active">
      <div class="card bg-info opacity-50">
        <div class="card-body h-full">
          <div class="min-h-[550px]"></div>
        </div>
      </div>
    </div>
  </div>

  <!-- MULTI FILE MODE -->
  <div v-else class="relative" :data-active="active" @dragenter.prevent="setActive" @dragover.prevent="setActive" @dragleave.prevent="setInactive" @drop.prevent="onDrop">
    <div class="card w-full border-2 border-dashed border-base-300 bg-white min-h-[300px]">
      <div class="card-body">
        <!-- Header -->
        <div class="flex items-center justify-between mb-3">
          <h3 class="font-semibold text-sm flex items-center gap-2">
            <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M9,16V10H5L12,3L19,10H15V16H9M5,20V18H19V20H5Z" /></svg>
            Dateien zum Hochladen ({{ (files as File[]).length }}/{{ maxFiles }})
          </h3>
        </div>

        <!-- Info Banner -->
        <div v-if="(files as File[]).length > 0" class="alert alert-info py-2 px-3 text-xs mb-3">
          <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-4 w-4" fill="none" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          <span>Diese Dateien wurden noch nicht gespeichert. Bitte Metadaten prüfen und „Alle hochladen" klicken.</span>
        </div>

        <!-- Empty state -->
        <div v-if="(files as File[]).length === 0" class="flex flex-col justify-center items-center py-12">
          <svg class="h-10 w-10 opacity-50" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M9,16V10H5L12,3L19,10H15V16H9M5,20V18H19V20H5Z" /></svg>
          <div class="my-4 text-sm opacity-70">Bilder hier reinziehen oder Dateien auswählen</div>
          <input type="file" multiple class="file-input file-input-bordered file-input-sm" @change.prevent="onInputChange"/>
        </div>

        <!-- Tile Grid -->
        <div v-else>
          <div class="grid 2xl:grid-cols-5 xl:grid-cols-4 lg:grid-cols-3 sm:grid-cols-2 grid-cols-2 gap-3">
            <div v-for="(f, index) in (files as File[])" :key="index" class="relative">
              <button @click.prevent="emit('select', index)" class="w-full">
                <div
                  class="w-full aspect-square bg-base-100 rounded-lg overflow-hidden flex items-center justify-center border-2 transition-all relative"
                  :class="selectedIndex === index ? 'border-warning outline outline-2 outline-warning' : 'border-base-300 hover:border-info'"
                >
                  <img class="w-full h-full object-contain" :src="fileSrc(f)" :alt="f.name">
                  <!-- Status Badge (top-left) -->
                  <div class="absolute top-1 left-1">
                    <!-- Pending / not started -->
                    <div v-if="!itemStatuses[index] || itemStatuses[index] === 'pending'" class="badge badge-ghost gap-1 text-xs">
                      <svg class="h-3.5 w-3.5 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M9,16V10H5L12,3L19,10H15V16H9M5,20V18H19V20H5Z" /></svg>
                    </div>
                    <!-- Uploading -->
                    <div v-else-if="itemStatuses[index] === 'uploading'" class="badge badge-info gap-1 text-xs">
                      <span class="loading loading-spinner" style="width: 14px; height: 14px;"></span>
                    </div>
                    <!-- Done -->
                    <div v-else-if="itemStatuses[index] === 'done'" class="badge badge-success gap-1 text-xs">
                      <svg class="h-3.5 w-3.5 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" /></svg>
                    </div>
                    <!-- Error -->
                    <div v-else-if="itemStatuses[index] === 'error'" class="badge badge-error gap-1 text-xs">
                      <svg class="h-3.5 w-3.5 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M13,13H11V7H13M13,17H11V15H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z" /></svg>
                    </div>
                  </div>
                </div>
                <div class="text-center mt-1">
                  <p class="text-xs truncate max-w-[140px] mx-auto">{{ f.name }}</p>
                  <p class="text-[10px] opacity-50">{{ formatSize(f.size) }}</p>
                </div>
              </button>
              <!-- Primary selection radio (only with parent) -->
              <button
                v-if="primaryIndex !== null"
                class="mt-1 w-full flex items-center justify-center gap-1 py-1 rounded text-[11px] transition-all cursor-pointer"
                :class="primaryIndex === index ? 'bg-info/20 text-info font-semibold' : 'hover:bg-base-200 opacity-60 hover:opacity-100'"
                @click.prevent="emit('set-primary', index)"
              >
                <svg v-if="primaryIndex === index" class="h-3.5 w-3.5 fill-info" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M10,17L5,12L6.41,10.58L10,14.17L17.59,6.58L19,8M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z" /></svg>
                <svg v-else class="h-3.5 w-3.5 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z" /></svg>
                <span>{{ primaryIndex === index ? 'Ausgewählt' : 'Auswählen' }}</span>
              </button>
              <!-- Remove button (same icon as single mode) -->
              <button class="btn btn-circle btn-error btn-xs absolute top-1 right-1" @click.prevent="removeFile(index)">
                <svg class="w-3 h-3 fill-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" /></svg>
              </button>
            </div>
          </div>

          <!-- Add more files -->
          <div class="mt-4 flex justify-center">
            <label class="btn btn-sm btn-outline btn-info">
              <svg class="h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z" /></svg>
              Weitere Dateien hinzufügen
              <input type="file" multiple class="hidden" @change.prevent="onInputChange"/>
            </label>
          </div>
        </div>
      </div>
    </div>

    <!-- Drag overlay -->
    <div class="absolute top-0 bottom-0 left-0 right-0 pointer-events-none" v-if="active">
      <div class="card bg-info opacity-50 h-full">
        <div class="card-body h-full flex items-center justify-center">
          <span class="text-lg font-bold text-white">Dateien hier ablegen</span>
        </div>
      </div>
    </div>
  </div>
</template>
